import { Inject, Injectable } from '@angular/core';
import { caller } from '@gelato-api-ui/core/api/caller';
import { callerBlob } from '@gelato-api-ui/core/api/caller-blob';
import { designEditorBackgroundColor } from '@gelato-api-ui/core/design-editor/constants/desgin-editor-background-color';
import { DesignStructure } from '@gelato-api-ui/core/designs/design-structure';
import { PreflightProductDefaultScenesRequest } from '@gelato-api-ui/core/preflight/preflight-product-default-scenes-request';
import { PreflightProductDefaultScenesResponse } from '@gelato-api-ui/core/preflight/preflight-product-default-scenes-response';
import { PreflightProductImageRequest } from '@gelato-api-ui/core/preflight/preflight-product-image-request';
import { PreflightProductImageResponse } from '@gelato-api-ui/core/preflight/preflight-product-image-response';
import { PREFLIGHT_CDN_BASE_URL } from '@gelato-api-ui/core/preflight/tokens/preflight.tokens';
import { ProductVariantAdditionalParams } from '@gelato-api-ui/core/product-catalogue/product-variant-additional-params';
import { Observable, of } from 'rxjs';
import { AjaxRequest } from 'rxjs/ajax';
import { catchError, map, retry } from 'rxjs/operators';
import { PreflightPdfProductTemplateRequest } from '../preflight-pdf-product-template-request';
import {
  PreflightProductPreviewScenesRequest,
  PreflightProductPreviewScenesResponse,
} from '../preflight-product-preview-scenes-response';
import { PreflightProductSceneImagesRequest } from '../preflight-product-scene_images-request';
import { PreflightProductSceneImagesResponse } from '../preflight-product-scene_images-response';

@Injectable({ providedIn: 'root' })
export class PreflightCdnService {
  get: <T>(url: string, options?: AjaxRequest) => Observable<{ data: T }>;
  getBlob: <T>(url: string, options?: AjaxRequest) => Observable<Blob>;

  constructor(@Inject(PREFLIGHT_CDN_BASE_URL) private readonly baseUrl: string) {
    this.get = caller(this.baseUrl, 'GET');
    this.getBlob = callerBlob(this.baseUrl, 'GET');
  }

  getEditorPagesProductPreviewScenes(
    productUid: string,
    additionalParams?: ProductVariantAdditionalParams,
  ): Observable<PreflightProductPreviewScenesResponse> {
    const body: PreflightProductPreviewScenesRequest = {
      product_uid: productUid,
      page_count: additionalParams?.pageCount,
      width: additionalParams?.width,
      height: additionalParams?.height,
      background_color: encodeURIComponent(designEditorBackgroundColor),
    };

    return this.get<PreflightProductPreviewScenesResponse>(`/product/preview/scenes/editor`, { body }).pipe(
      map(r => r.data),
    );
  }

  getProductImage(
    productUid: string,
    additionalParams?: ProductVariantAdditionalParams,
  ): Observable<PreflightProductImageResponse> {
    const body: PreflightProductImageRequest = {
      product_uid: productUid,
      width: additionalParams?.width,
      height: additionalParams?.height,
      scene: additionalParams?.scene,
    };
    return this.get<PreflightProductImageResponse>('/product/image', {
      body,
    }).pipe(map(data => data.data));
  }

  getProductSceneImages(
    productUid: string,
    additionalParams?: { width?: number; height?: number },
  ): Observable<PreflightProductSceneImagesResponse> {
    const body: PreflightProductSceneImagesRequest = {
      product_uid: productUid,
      width: additionalParams?.width,
      height: additionalParams?.height,
    };
    return this.get<PreflightProductSceneImagesResponse>('/product/scene_images', {
      body,
    }).pipe(map(data => data.data));
  }

  getProductDefaultScenes(productUid: string): Observable<PreflightProductDefaultScenesResponse> {
    const body: PreflightProductDefaultScenesRequest = { product_uid: productUid };
    return this.get<PreflightProductDefaultScenesResponse>('/product/preview/scenes', {
      body,
    }).pipe(map(data => data.data));
  }

  getProductTemplate<
    T extends PreflightPdfProductTemplateRequest,
    E extends T['type'] extends 'json' ? DesignStructure : Blob,
  >(request: T): Observable<E> {
    if (request.type === 'json') {
      return this.get('/product/template', { body: request }).pipe(
        retry(2),
        map(res => res.data),
        catchError(() => of(null)),
      );
    }

    return this.getBlob('/product/template', { body: request }).pipe(
      retry(2),
      catchError(() => of(null)),
    );
  }

  getProductMockupTemplate(request: { width: number; height: number }): Observable<DesignStructure> {
    return this.get<DesignStructure>('/product/mockup/template', { body: request }).pipe(
      map(r => r.data),
      catchError(() => of(null)),
    );
  }

  downloadProductTemplate(productUid: string, pageCount: number = null): void {
    window.open(this.getProductTemplateUrl(productUid, pageCount || undefined), '_blank');
  }

  getProductTemplateUrl(productUid: string, pageCount: number = null): string {
    const paramsPairs = [];

    if (productUid) {
      paramsPairs.push(`product_uid=${productUid}`);
    }

    if (pageCount) {
      paramsPairs.push(`page_count=${pageCount}`);
    }

    return `${this.baseUrl}/product/template?${paramsPairs.join('&')}`;
  }

  getEmbroideryPalette(): Observable<string[]> {
    let url = '/embroidery/palette?hex=true';
    return this.get<string[]>(url).pipe(map(r => r.data));
  }
}
